import { RebateOverview, RebateOverviewFilter } from "@/gql/graphql";
import { useContext, useCallback, useState, useMemo } from "react";
import { RebatesFilterContextType } from "../types/types";
import { RebatesFilterContext } from "./rebates-filter/RebatesFilterContext";
import { RebatesFilter } from "./rebates-filter/RebatesFilter";
import { CardBody, CardHeader, FlatButton } from "@stenajs-webui/elements";
import { StandardTable } from "@stenajs-webui/grid";
import { Box } from "@stenajs-webui/core";
import { createHandleRebatesTableConfig } from "../config/HandleRebatesTableConfig";
import { HandleRebatesDrawer } from "./HandleRebatesDrawer";
import {
  HandleRebateFormModal,
  RebateModalMode,
} from "./HandleRebateFormModal";
import { AuthorizePayoutModal } from "./AuthorizePayoutModal";
import { useRebateOverview } from "../hooks/UseRebateOverview";
import { pluralize } from "@/lib/formatters/GeneralStringFormatting";
import { rebatesTableMaxHeight } from "../lib/layout";

export const HandleRebatesView = () => {
  const { rebateOverview, loadRebateOverview, loading } = useRebateOverview();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthorizeModalOpen, setIsAuthorizeModalOpen] = useState(false);
  const [rebatesModalMode, setRebatesModalMode] =
    useState<RebateModalMode>("RELEASE");

  const { filterState, setFilterIsOpen } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;

  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [selectedPaymentStatus, setSelectedPaymentStatus] =
    useState<RebateOverview>();

  const noFiltersSelected =
    !filterState.selectedAgreementNumber &&
    filterState.selectedSalesRegions.length === 0 &&
    !filterState.selectedCustomer &&
    !filterState.selectedSeller &&
    !filterState.selectedValidity &&
    !filterState.selectedYear &&
    !filterState.selectedGoalStatusCodes.length &&
    filterState.selectedTerms.length === 0;

  const onSearch = useCallback(() => {
    if (noFiltersSelected) {
      return;
    }

    const {
      selectedAgreementNumber,
      selectedSalesRegions,
      selectedCustomer,
      selectedSeller,
      selectedYear,
      selectedValidity,
      selectedTerms,
      selectedGoalStatusCodes,
    } = filterState;

    const agreementNumber = parseInt(selectedAgreementNumber);

    const filter: RebateOverviewFilter = {
      ...(Number.isFinite(agreementNumber) && {
        rebateAgreementNumber: agreementNumber,
      }),
      ...(selectedCustomer?.value && {
        customerNumber: parseInt(selectedCustomer.value),
      }),
      ...(selectedSeller?.value && { sellerId: selectedSeller.value }),
      ...(selectedValidity?.startDate && {
        validFrom: selectedValidity.startDate.toISOString(),
      }),
      ...(selectedYear?.value && { year: selectedYear.value }),
      ...(selectedValidity?.endDate && {
        validTo: selectedValidity.endDate.toISOString(),
      }),
      ...(selectedSalesRegions.length > 0 && {
        salesRegionCodes: selectedSalesRegions.map((r) => parseInt(r.code)),
      }),
      ...(selectedTerms.length > 0 && {
        rebateTermCodes: selectedTerms.map((t) => t.id),
      }),
      ...(selectedGoalStatusCodes.length > 0 && {
        goalStatusCodes: selectedGoalStatusCodes,
      }),
    };

    loadRebateOverview({ variables: { filter } });
  }, [filterState, loadRebateOverview, noFiltersSelected]);

  const onOpenDrawer = useCallback((item: RebateOverview) => {
    setSelectedPaymentStatus(item);
    setShowDetailsDrawer(true);
  }, []);
  const onHandleRebate = useCallback(
    (item: RebateOverview, mode: RebateModalMode) => {
      setSelectedPaymentStatus(item);
      setRebatesModalMode(mode);
      setIsModalOpen(true);
    },
    []
  );
  const onAuthorize = useCallback((item: RebateOverview) => {
    setSelectedPaymentStatus(item);
    setIsAuthorizeModalOpen(true);
  }, []);
  const config = useMemo(
    () =>
      createHandleRebatesTableConfig(onOpenDrawer, onHandleRebate, onAuthorize),
    [onOpenDrawer, onHandleRebate, onAuthorize]
  );

  return (
    <>
      {isModalOpen && (
        <HandleRebateFormModal
          isOpen={isModalOpen}
          rebate={selectedPaymentStatus}
          onDone={() => setIsModalOpen(false)}
          mode={rebatesModalMode}
        />
      )}
      {isAuthorizeModalOpen && (
        <AuthorizePayoutModal
          isOpen={isAuthorizeModalOpen}
          rebate={selectedPaymentStatus}
          onDone={() => setIsAuthorizeModalOpen(false)}
        />
      )}

      <CardHeader
        text={
          rebateOverview.length > 0
            ? pluralize(rebateOverview.length, " rebate goal", " rebate goals")
            : "Rebate goals"
        }
      />
      <RebatesFilter onSearch={onSearch} showGoalStatus showAgreementNumber />
      <HandleRebatesDrawer
        selectedPaymentStatus={selectedPaymentStatus}
        isOpen={showDetailsDrawer}
        onRequestClose={() => setShowDetailsDrawer(false)}
      />
      <Box overflow={"auto"} maxHeight={rebatesTableMaxHeight}>
        <CardBody>
          <StandardTable
            config={config}
            items={rebateOverview}
            loading={loading}
            noItemsLabel={
              noFiltersSelected
                ? "Please select a filter to search"
                : "No rebate agreement goals to show"
            }
            noItemsContentRight={
              <FlatButton
                label={"Open filter"}
                onClick={() => setFilterIsOpen(true)}
              />
            }
          />
        </CardBody>
      </Box>
    </>
  );
};
